/**
 * Mock Workspace Tool Client (Reminders, Notes, Daily Standup & Unified Aggregator)
 * Implements IWorkspaceClient interface with LocalStorage persistence.
 */

import {
  ApiEnvelope,
  DailyGroup,
  ReminderItem,
  NoteItem,
  AttentionItem,
  HoldInfo,
  StateSuggestion,
  SentryOrExtSignal,
  WorkContextGraph,
  ResourceRelation,
  ManualWorkHub,
  LlmWikiPage,
} from '../../domain/types';
import {
  INITIAL_REMINDERS,
  INITIAL_NOTES,
  INITIAL_DAILY_GROUPS,
  ITEMS,
  HOLD_DATA,
  SUGGEST_DATA,
  EXT_SIGNALS,
  PR_TALK_MAP,
} from '../../data/initialData';
import { StorageService } from '../storageService';
import { IWorkspaceClient, ClientConfig } from './types';
import { simulateNetwork, DEFAULT_CONFIG } from './mockClientUtils';
import { upsertResourceRelation, type WorkspaceBundle } from '@zunelo/shared';
import {
  buildRadarAiReply,
  type RadarAiAttachment,
  type WorkThreadSourceKind,
} from '../../domain/radarThreads';

const WORK_CONTEXT_STORAGE_KEY = 'workContextGraph';

function emptyGraph(): WorkContextGraph {
  return { relations: [], manualHubs: [] };
}

function loadPersistedGraph(): WorkContextGraph {
  const stored = StorageService.get<WorkContextGraph>(WORK_CONTEXT_STORAGE_KEY, emptyGraph());
  return {
    relations: Array.isArray(stored.relations) ? stored.relations : [],
    manualHubs: Array.isArray(stored.manualHubs) ? stored.manualHubs : [],
  };
}

export class MockWorkspaceClient implements IWorkspaceClient {
  private reminders: ReminderItem[];
  private notes: NoteItem[];
  private dailyGroups: DailyGroup[];
  private dailyDraft: string | null;
  private items: AttentionItem[];
  private workContextGraph: WorkContextGraph;
  private llmWiki: LlmWikiPage[];
  private config: ClientConfig;

  constructor(config: ClientConfig = DEFAULT_CONFIG) {
    this.config = config;
    this.reminders = StorageService.get<ReminderItem[]>('reminders', INITIAL_REMINDERS);
    this.notes = StorageService.get<NoteItem[]>('notes', INITIAL_NOTES);
    this.dailyGroups = StorageService.get<DailyGroup[]>('dailyGroups', INITIAL_DAILY_GROUPS);
    this.dailyDraft = StorageService.get<string | null>('dailyDraft', null);
    this.workContextGraph = loadPersistedGraph();
    this.llmWiki = StorageService.get<LlmWikiPage[]>('llmWiki', []);
    this.items = [...ITEMS];
  }

  async getWorkspaceBundle(): Promise<ApiEnvelope<WorkspaceBundle>> {
    return simulateNetwork(() => {
      const sent = StorageService.get<boolean>('dailySent', false);
      return {
        reminders: [...this.reminders],
        notes: [...this.notes],
        llmWiki: [...this.llmWiki],
        // Domain `DailyGroup.tone` (Tone) is a superset of the shared
        // `DailyGroupTone`; mock fixtures never use the extra values.
        dailyGroups: [...this.dailyGroups] as unknown as WorkspaceBundle['dailyGroups'],
        dailyDraft: this.dailyDraft,
        dailySent: { sent, sentAt: sent ? new Date().toISOString() : null },
        workContext: {
          relations: [...this.workContextGraph.relations],
          manualHubs: [...(this.workContextGraph.manualHubs ?? [])],
        },
        sections: {
          reminders: { status: 'ok' },
          notes: { status: 'ok' },
          llmWiki: { status: 'ok' },
          dailyGroups: { status: 'ok' },
          workContext: { status: 'ok' },
        },
      };
    }, this.config);
  }

  async getReminders(): Promise<ApiEnvelope<ReminderItem[]>> {
    return simulateNetwork(() => [...this.reminders], this.config);
  }

  async saveReminder(reminder: ReminderItem): Promise<ApiEnvelope<ReminderItem>> {
    return simulateNetwork(() => {
      const idx = this.reminders.findIndex((r) => r.id === reminder.id);
      if (idx >= 0) {
        this.reminders[idx] = reminder;
      } else {
        this.reminders.push(reminder);
      }
      StorageService.set('reminders', this.reminders);
      return { ...reminder };
    }, this.config);
  }

  async toggleReminder(id: string, done?: boolean): Promise<ApiEnvelope<ReminderItem>> {
    return simulateNetwork(() => {
      const rem = this.reminders.find((r) => r.id === id);
      if (!rem) throw new Error(`Reminder ${id} not found`);
      rem.done = typeof done === 'boolean' ? done : !rem.done;
      StorageService.set('reminders', this.reminders);
      return { ...rem };
    }, this.config);
  }

  async deleteReminder(id: string): Promise<ApiEnvelope<{ deleted: boolean }>> {
    return simulateNetwork(() => {
      this.reminders = this.reminders.filter((r) => r.id !== id);
      StorageService.set('reminders', this.reminders);
      return { deleted: true };
    }, this.config);
  }

  async getNotes(): Promise<ApiEnvelope<NoteItem[]>> {
    return simulateNetwork(() => [...this.notes], this.config);
  }

  async saveNote(note: NoteItem): Promise<ApiEnvelope<NoteItem>> {
    return simulateNetwork(() => {
      const idx = this.notes.findIndex((n) => n.id === note.id);
      if (idx >= 0) {
        this.notes[idx] = note;
      } else {
        this.notes.unshift(note);
      }
      StorageService.set('notes', this.notes);
      return { ...note };
    }, this.config);
  }

  async deleteNote(id: string): Promise<ApiEnvelope<{ deleted: boolean }>> {
    return simulateNetwork(() => {
      this.notes = this.notes.filter((n) => n.id !== id);
      StorageService.set('notes', this.notes);
      return { deleted: true };
    }, this.config);
  }

  async getLlmWiki(): Promise<ApiEnvelope<LlmWikiPage[]>> {
    return simulateNetwork(
      () => [...this.llmWiki].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)),
      this.config,
    );
  }
  async getLlmWikiPage(id: string): Promise<ApiEnvelope<LlmWikiPage | null>> {
    return {
      data: this.llmWiki.find((page) => page.id === id) ?? null,
      serverTime: new Date().toISOString(),
    };
  }
  async getLlmWikiPageBatch(
    cursor = '0',
    limit = 200,
  ): Promise<ApiEnvelope<import('@zunelo/shared').LlmWikiPageList>> {
    const start = Number.parseInt(cursor, 10) || 0;
    const pages = this.llmWiki.slice(start, start + limit);
    return {
      data: {
        pages,
        nextCursor:
          start + pages.length < this.llmWiki.length ? String(start + pages.length) : null,
        total: this.llmWiki.length,
        revision: String(this.llmWiki.length),
      },
      serverTime: new Date().toISOString(),
    };
  }
  async getLlmWikiGraphManifest(): Promise<
    ApiEnvelope<import('@zunelo/shared').LlmWikiTopologyManifest>
  > {
    return {
      data: {
        graphRevision: 'empty',
        layoutVersion: 'wiki-layout-v1',
        nodes: [],
        edges: [],
        contexts: [],
        totals: { nodes: 0, links: 0, contexts: 0 },
      },
      serverTime: new Date().toISOString(),
    };
  }
  async getLlmWikiNodeMetadata(): Promise<
    ApiEnvelope<import('@zunelo/shared').LlmWikiNodeMetadataPage>
  > {
    return {
      data: { nodes: [], nextCursor: null, total: 0, revision: '0' },
      serverTime: new Date().toISOString(),
    };
  }

  async saveLlmWikiPage(page: LlmWikiPage): Promise<ApiEnvelope<LlmWikiPage>> {
    return simulateNetwork(() => {
      const nowIso = new Date().toISOString();
      const index = this.llmWiki.findIndex((item) => item.id === page.id);
      const existing = index >= 0 ? this.llmWiki[index] : undefined;
      const saved = {
        ...page,
        title: page.title.trim(),
        summary: page.summary.trim(),
        content: page.content.trim(),
        tags: page.tags.map((tag) => tag.trim()).filter(Boolean),
        createdAt: existing?.createdAt ?? page.createdAt ?? nowIso,
        updatedAt: nowIso,
      };
      if (index >= 0) this.llmWiki[index] = saved;
      else this.llmWiki.unshift(saved);
      StorageService.set('llmWiki', this.llmWiki);
      return { ...saved, tags: [...saved.tags] };
    }, this.config);
  }

  async deleteLlmWikiPage(id: string): Promise<ApiEnvelope<{ deleted: boolean }>> {
    return simulateNetwork(() => {
      this.llmWiki = this.llmWiki.filter((page) => page.id !== id);
      StorageService.set('llmWiki', this.llmWiki);
      return { deleted: true };
    }, this.config);
  }

  async getDailyGroups(): Promise<ApiEnvelope<DailyGroup[]>> {
    return simulateNetwork(() => [...this.dailyGroups], this.config);
  }

  async saveDailyGroup(group: DailyGroup): Promise<ApiEnvelope<DailyGroup>> {
    return simulateNetwork(() => {
      const idx = this.dailyGroups.findIndex((g) => g.id === group.id);
      if (idx >= 0) {
        this.dailyGroups[idx] = group;
      } else {
        this.dailyGroups.push(group);
      }
      StorageService.set('dailyGroups', this.dailyGroups);
      return { ...group };
    }, this.config);
  }

  async sendDaily(_channel: string): Promise<ApiEnvelope<{ sent: boolean; sentAt: string }>> {
    return simulateNetwork(() => {
      StorageService.set('dailySent', true);
      StorageService.set('dailyDraft', null);
      this.dailyDraft = null;
      return { sent: true, sentAt: new Date().toISOString() };
    }, this.config);
  }

  async saveDailyDraft(markdown: string): Promise<ApiEnvelope<{ saved: boolean }>> {
    return simulateNetwork(() => {
      this.dailyDraft = markdown;
      StorageService.set('dailyDraft', markdown);
      return { saved: true };
    }, this.config);
  }

  async getDailyDraft(): Promise<ApiEnvelope<string | null>> {
    return simulateNetwork(() => this.dailyDraft, this.config);
  }

  async getDailyStatus(): Promise<ApiEnvelope<{ sent: boolean; sentAt: string | null }>> {
    return simulateNetwork(() => {
      const sent = StorageService.get<boolean>('dailySent', false);
      return { sent, sentAt: sent ? new Date().toISOString() : null };
    }, this.config);
  }

  async clearDailySent(): Promise<ApiEnvelope<{ cleared: boolean }>> {
    return simulateNetwork(() => {
      StorageService.set('dailySent', false);
      return { cleared: true };
    }, this.config);
  }

  async bootstrapWorkspace(doc: {
    reminders: ReminderItem[];
    notes: NoteItem[];
    dailyGroups: DailyGroup[];
    dailyDraft: string | null;
    dailySentAt?: string | null;
    llmWiki?: LlmWikiPage[];
  }): Promise<ApiEnvelope<unknown>> {
    return simulateNetwork(() => {
      this.reminders = [...doc.reminders];
      this.notes = [...doc.notes];
      this.dailyGroups = [...doc.dailyGroups];
      this.dailyDraft = doc.dailyDraft;
      this.llmWiki = doc.llmWiki ? [...doc.llmWiki] : this.llmWiki;
      StorageService.set('reminders', this.reminders);
      StorageService.set('notes', this.notes);
      StorageService.set('dailyGroups', this.dailyGroups);
      StorageService.set('dailyDraft', this.dailyDraft);
      StorageService.set('llmWiki', this.llmWiki);
      StorageService.set('dailySent', Boolean(doc.dailySentAt));
      return { ...doc, updatedAt: new Date().toISOString() };
    }, this.config);
  }

  async getAllUnifiedAttentionItems(): Promise<ApiEnvelope<AttentionItem[]>> {
    return simulateNetwork(() => [...this.items], this.config);
  }

  async getWorkContextGraph(): Promise<ApiEnvelope<WorkContextGraph>> {
    return simulateNetwork(
      () => ({
        relations: [...this.workContextGraph.relations],
        manualHubs: [...(this.workContextGraph.manualHubs ?? [])],
      }),
      this.config,
    );
  }

  async createManualWorkHub(input: { title: string }): Promise<ApiEnvelope<ManualWorkHub>> {
    return simulateNetwork(() => {
      const title = input.title.trim();
      if (!title) throw new Error('Hub title is required.');
      const hub: ManualWorkHub = {
        id: `hub-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
        title,
        createdAt: new Date().toISOString(),
      };
      this.workContextGraph = {
        relations: this.workContextGraph.relations,
        manualHubs: [...(this.workContextGraph.manualHubs ?? []), hub],
      };
      StorageService.set(WORK_CONTEXT_STORAGE_KEY, this.workContextGraph);
      return hub;
    }, this.config);
  }

  async upsertWorkContextRelation(
    relation: ResourceRelation,
  ): Promise<ApiEnvelope<WorkContextGraph>> {
    return simulateNetwork(() => {
      this.workContextGraph = {
        relations: upsertResourceRelation(this.workContextGraph.relations, relation),
        manualHubs: [...(this.workContextGraph.manualHubs ?? [])],
      };
      StorageService.set(WORK_CONTEXT_STORAGE_KEY, this.workContextGraph);
      return {
        relations: [...this.workContextGraph.relations],
        manualHubs: [...(this.workContextGraph.manualHubs ?? [])],
      };
    }, this.config);
  }

  async askRadarAssistant(input: {
    question: string;
    contexts: Array<{
      key: string;
      title: string;
      summary: string;
      nextStep: string;
      sources: string[];
    }>;
  }): Promise<ApiEnvelope<{ answer: string; provider?: string; model?: string }>> {
    return simulateNetwork(() => {
      const attachments: RadarAiAttachment[] = input.contexts.map((ctx) => ({
        id: ctx.key,
        key: ctx.key,
        title: ctx.title,
        summary: ctx.summary,
        nextStep: ctx.nextStep,
        sources: ctx.sources as WorkThreadSourceKind[],
      }));
      return {
        answer: buildRadarAiReply(input.question, attachments),
        provider: 'mock',
        model: 'local-briefing',
      };
    }, this.config);
  }

  async getHoldData(): Promise<ApiEnvelope<Record<string, HoldInfo>>> {
    return simulateNetwork(() => ({ ...HOLD_DATA }), this.config);
  }

  async getSuggestions(): Promise<ApiEnvelope<Record<string, StateSuggestion>>> {
    return simulateNetwork(() => ({ ...SUGGEST_DATA }), this.config);
  }

  async getExternalSignals(): Promise<ApiEnvelope<Record<string, SentryOrExtSignal[]>>> {
    return simulateNetwork(() => ({ ...EXT_SIGNALS }), this.config);
  }

  async getPrTalkMap(): Promise<
    ApiEnvelope<Record<string, Array<{ ch: string; text: string; when: string; id: string }>>>
  > {
    return simulateNetwork(() => ({ ...PR_TALK_MAP }), this.config);
  }
}
